/**
 * Sydney Morning Herald RSS Feed Provider
 * 
 * Fetches articles from Sydney Morning Herald RSS feeds.
 * Part of Nine Entertainment network.
 * Focuses on NSW and national news coverage.
 * 
 * RSS Feed: https://www.smh.com.au/rss/feed.xml
 */

import relevanceEngine from '../relevanceEngine.js';

class SMHNewsFeedProvider {
  constructor() {
    this.name = 'Sydney Morning Herald';
    this.baseUrl = 'https://www.smh.com.au/rss';
    this.isEnabled = true;
    this.localProxyAvailable = false; // Will be set after connectivity test
    this.lastProxyTest = 0; // Rate limiting for proxy tests
    
    // RSS proxy endpoint from environment variable with fallback
    const rssProxyUrl = process.env.REACT_APP_RSS_PROXY_URL || 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/rssProxy';
    this.localProxy = `${rssProxyUrl}?url=`;
    
    console.log('📡 SMH RSS Provider initialized with RSS Proxy:', rssProxyUrl);
    
    // SMH specific RSS endpoints - Updated with working URLs
    this.endpoints = {
      main: 'https://www.smh.com.au/rss/feed.xml',
      national: 'https://www.smh.com.au/rss/national.xml',
      nsw: 'https://www.smh.com.au/rss/feed.xml' // Use main feed as NSW-specific feed is broken
      // REMOVED: politics and world feeds - focus on crisis-relevant content only
    };
  }

  /**
   * Test local proxy connectivity with rate limiting
   */
  async testLocalProxyConnectivity() {
    // Rate limiting: Only test once per minute
    const now = Date.now();
    if (this.lastProxyTest && (now - this.lastProxyTest) < 60000) {
      console.log('⏱️ SMH proxy test skipped - rate limited (last test was less than 1 minute ago)');
      return;
    }
    
    this.lastProxyTest = now;
    console.log('🔬 Testing SMH proxy connectivity...');
    
    try {
      const rssProxyUrl = process.env.REACT_APP_RSS_PROXY_URL || 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/rssProxy';
      const response = await fetch(rssProxyUrl, {
        timeout: 5000
      });
      
      if (response.ok) {
        console.log('✅ SMH proxy is accessible');
        this.localProxyAvailable = true;
      } else {
        console.warn('⚠️ SMH proxy health check failed:', response.status);
        this.localProxyAvailable = false;
      }
    } catch (error) {
      console.warn('⚠️ SMH proxy connection failed:', error.message);
      this.localProxyAvailable = false;
    }
  }

  /**
   * Fetch articles from SMH RSS feeds
   */
  async fetchArticles(options = {}) {
    console.log(`🚨 SMH Provider: Fetching articles - ${new Date().toISOString()}`);
    console.log('🔍 SMH - Fetching with options:', options);
    
    let allArticles = [];
    let searchedSources = []; // Track what sources we actually searched
    
    try {
      const feeds = this.buildFeedUrls(options);
      console.log(`📡 SMH: Attempting to fetch from ${feeds.length} feeds using RSS PROXY...`);

      for (const feed of feeds) {
        // Track this source as searched
        searchedSources.push({
          name: feed.name,
          type: feed.type,
          status: 'attempted'
        });

        try {
          console.log(`📰 SMH: Fetching from: ${feed.name}`);
          const articles = await this.fetchFromFeedWithProxy(feed.url);
          
          if (articles && articles.length > 0) {
            // Add feed metadata to articles
            const enrichedArticles = articles.map(article => ({
              ...article,
              feedSource: feed.name,
              feedType: feed.type,
              feedLocation: feed.location || null,
              provider: 'Sydney Morning Herald'
            }));
            
            allArticles.push(...enrichedArticles);
            console.log(`✅ SMH ${feed.name}: ${articles.length} articles`);
            
            // Update search status
            const sourceIndex = searchedSources.findIndex(s => s.name === feed.name);
            if (sourceIndex >= 0) {
              searchedSources[sourceIndex].status = 'success';
              searchedSources[sourceIndex].articleCount = articles.length;
            }
          } else {
            console.log(`⚠️ SMH ${feed.name}: No articles found`);
            // Update search status
            const sourceIndex = searchedSources.findIndex(s => s.name === feed.name);
            if (sourceIndex >= 0) {
              searchedSources[sourceIndex].status = 'no_results';
            }
          }
          
          // Rate limiting: Small delay between feed requests
          await this.delay(1000); // More conservative for commercial feeds
          
        } catch (feedError) {
          console.warn(`⚠️ SMH feed error for ${feed.name}:`, feedError.message);
          // Update search status
          const sourceIndex = searchedSources.findIndex(s => s.name === feed.name);
          if (sourceIndex >= 0) {
            searchedSources[sourceIndex].status = 'error';
            searchedSources[sourceIndex].error = feedError.message;
          }
        }
      }

      // Store search metadata for debugging
      this.lastSearchMetadata = {
        timestamp: new Date(),
        searchedSources: searchedSources,
        totalArticles: allArticles.length,
        feedsAttempted: feeds.length,
        feedsSuccessful: searchedSources.filter(s => s.status === 'success').length
      };

      console.log(`✅ SMH: Retrieved ${allArticles.length} total articles from ${feeds.length} feeds`);
      return allArticles;

    } catch (error) {
      console.error('❌ SMH Provider - Critical error:', error);
      
      // Store error metadata
      this.lastSearchMetadata = {
        timestamp: new Date(),
        searchedSources: searchedSources,
        totalArticles: 0,
        error: error.message,
        feedsAttempted: 0,
        feedsSuccessful: 0
      };
      
      return [];
    }
  }

  /**
   * Get metadata about the last search operation
   */
  getLastSearchMetadata() {
    return this.lastSearchMetadata || {
      timestamp: null,
      searchedSources: [],
      totalArticles: 0,
      feedsAttempted: 0,
      feedsSuccessful: 0
    };
  }

  /**
   * Fetch from RSS feed with local proxy ONLY
   */
  async fetchFromFeedWithProxy(feedUrl) {
    console.log('🚨 SMH DEBUG: fetchFromFeedWithProxy called with:', feedUrl);
    console.log('🚨 SMH DEBUG: Local proxy URL configured as:', this.localProxy);
    console.log('🚨 SMH DEBUG: Local proxy available:', this.localProxyAvailable);
    
    // RSS Proxy should always be available in production - removing unnecessary availability check
    // if (this.localProxyAvailable === false) {
    //   console.warn('⚠️ SMH LOCAL PROXY MARKED AS UNAVAILABLE - RETURNING EMPTY RESULTS');
    //   return [];
    // }
    
    // Try local proxy ONLY - NO FALLBACKS TO AVOID CORS SPAM
    try {
      console.log(`🏠 SMH ATTEMPTING LOCAL PROXY FOR: ${feedUrl}`);
      const proxyUrl = `${this.localProxy}${encodeURIComponent(feedUrl)}`;
      console.log(`🔗 SMH LOCAL PROXY URL: ${proxyUrl}`);
      
      const response = await Promise.race([
        fetch(proxyUrl, {
          headers: {
            'Accept': 'application/xml, text/xml, application/rss+xml',
            'User-Agent': 'Crisis Compass Media Intelligence/1.0'
          }
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 15000) // 15 second timeout for commercial feeds
        )
      ]);

      console.log(`📡 SMH LOCAL PROXY RESPONSE STATUS: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        throw new Error(`Local proxy failed: HTTP ${response.status} ${response.statusText}`);
      }

      const xmlText = await response.text();
      console.log(`📄 SMH LOCAL PROXY RETURNED ${xmlText.length} BYTES`);
      
      // More thorough validation of RSS content
      if (!xmlText || xmlText.length < 100) {
        throw new Error('Local proxy returned empty or invalid RSS data');
      }
      
      const articles = this.parseRSSFeed(xmlText, feedUrl);
      console.log(`📰 SMH PARSED ${articles.length} ARTICLES FROM RSS`);
      
      // Mark proxy as working if we get here successfully
      this.localProxyAvailable = true;
      
      return articles;

    } catch (error) {
      console.error(`❌ SMH LOCAL PROXY FAILED for ${feedUrl}:`, error.message);
      
      // Mark proxy as potentially failed for future requests
      this.localProxyAvailable = false;
      
      return [];
    }
  }

  /**
   * Build RSS feed URLs based on search options
   */
  buildFeedUrls(options = {}) {
    const feeds = [];
    
    // Always include main feed
    feeds.push({
      name: 'SMH Main Feed',
      type: 'main',
      priority: 1,
      url: this.endpoints.main
    });
    
    feeds.push({
      name: 'SMH National News',
      type: 'national',
      priority: 1,
      url: this.endpoints.national
    });

    // Add NSW-specific content if relevant
    if (options.locations && options.locations.some(loc => 
      ['nsw', 'new south wales', 'sydney'].includes(loc.toLowerCase()))) {
      feeds.push({
        name: 'SMH NSW News',
        type: 'state_specific',
        priority: 1,
        location: 'NSW',
        url: this.endpoints.nsw
      });
    }

    // REMOVED: Politics feed - focus on crisis-relevant content only

    console.log(`📡 SMH: Built ${feeds.length} feed URLs`);
    return feeds;
  }

  /**
   * Parse RSS XML into article objects
   */
  parseRSSFeed(xmlText, feedUrl) {
    try {
      // Quick validation - check if it looks like XML
      if (!xmlText || !xmlText.trim().startsWith('<')) {
        console.warn('⚠️ SMH: Invalid XML response (likely HTML error page)');
        return [];
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
      
      // Check for parsing errors
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        console.warn('⚠️ SMH: XML parsing failed - malformed RSS feed');
        return [];
      }

      const items = xmlDoc.querySelectorAll('item');
      
      if (items.length === 0) {
        console.warn('⚠️ SMH: No RSS items found in feed');
        return [];
      }

      const articles = [];

      for (const item of items) {
        try {
          const article = this.parseRSSItem(item);
          if (article) {
            articles.push(article);
          }
        } catch (error) {
          // Don't spam console with individual item errors
          continue;
        }
      }

      return articles;

    } catch (error) {
      console.warn('⚠️ SMH: RSS parsing failed:', error.message);
      return [];
    }
  }

  /**
   * Parse individual RSS item
   */
  parseRSSItem(item) {
    try {
      // Extract basic fields
      const title = this.getTextContent(item, 'title');
      const link = this.getTextContent(item, 'link');
      const description = this.getTextContent(item, 'description');
      const pubDate = this.getTextContent(item, 'pubDate');
      const guid = this.getTextContent(item, 'guid');

      if (!title || !link) {
        return null; // Skip invalid items
      }

      // Parse publication date
      let publishedAt = null;
      if (pubDate) {
        publishedAt = new Date(pubDate);
        if (isNaN(publishedAt.getTime())) {
          publishedAt = new Date(); // Fallback to current time
        }
      } else {
        publishedAt = new Date();
      }

      // Clean up description (remove HTML tags)
      const cleanDescription = this.stripHtml(description);

      // Extract location from content (simple pattern matching)
      const location = this.extractLocation(title, cleanDescription);

      return {
        title: title.trim(),
        url: link.trim(),
        content: cleanDescription?.substring(0, 500) || '', // Limit content length
        source: 'Sydney Morning Herald',
        publishedAt: publishedAt,
        location: location,
        language: 'en',
        country: 'AU',
        sentiment: null, // Will be processed later
        relevanceScore: 0, // Will be calculated later
        keywords: [], // Will be extracted later
        category: 'news',
        guid: guid || link, // Use GUID for deduplication, fallback to link
        rawData: {
          originalDescription: description,
          pubDate: pubDate,
          feedUrl: item.baseURI || 'smh-news-rss'
        }
      };

    } catch (error) {
      console.error('❌ SMH: Error parsing RSS item:', error);
      return null;
    }
  }

  /**
   * Get text content from XML element
   */
  getTextContent(item, tagName) {
    const element = item.querySelector(tagName);
    return element ? element.textContent.trim() : null;
  }

  /**
   * Strip HTML tags from text
   */
  stripHtml(html) {
    if (!html) return '';
    
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  /**
   * Extract location from article text (Australian-focused pattern matching)
   */
  extractLocation(title, content) {
    const text = `${title} ${content}`.toLowerCase();
    
    // Australian cities and states (prioritized for SMH - NSW focus)
    const locations = [
      'sydney', 'melbourne', 'brisbane', 'perth', 'adelaide', 'darwin', 'canberra', 'hobart',
      'nsw', 'vic', 'qld', 'wa', 'sa', 'nt', 'act', 'tas',
      'new south wales', 'victoria', 'queensland', 'western australia',
      'south australia', 'northern territory', 'australian capital territory', 'tasmania',
      'newcastle', 'wollongong', 'central coast', 'illawarra', 'hunter valley',
      'blue mountains', 'north shore', 'eastern suburbs', 'western sydney'
    ];

    for (const location of locations) {
      if (text.includes(location)) {
        return location.charAt(0).toUpperCase() + location.slice(1);
      }
    }

    return null;
  }

  /**
   * Add delay for rate limiting
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get provider status
   */
  getStatus() {
    return {
      name: this.name,
      isEnabled: this.isEnabled,
      type: 'rss',
      cost: 'free',
      rateLimit: '1 request/second (conservative)',
      coverage: 'NSW and national news coverage',
      endpoints: Object.keys(this.endpoints).length,
      lastSearch: this.lastSearchMetadata
    };
  }

  /**
   * Test connection to SMH
   */
  async testConnection() {
    try {
      const articles = await this.fetchFromFeedWithProxy(this.endpoints.main);
      
      return {
        success: true,
        articlesFound: articles.length,
        message: `Successfully fetched ${articles.length} articles from SMH main feed`,
        endpoints: Object.keys(this.endpoints).length
      };

    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Failed to connect to SMH RSS feeds'
      };
    }
  }
}

export default SMHNewsFeedProvider;